import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { routes, handleHotUpdate } from 'vue-router/auto-routes'

import { useAuthStore } from '@/stores/auth'
import { DEV_TOOLS } from '@/utils/constants'

const REDIRECT_KEY = 'redirectUrl'
const LOGIN_ROUTE = '/auth/login'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

export function rememberRoute(fullPath: string): void {
  if (fullPath.startsWith('/auth/')) return
  localStorage.setItem(REDIRECT_KEY, fullPath)
}

/**
 * Exported so it can be tested directly rather than by driving a real router
 * through navigations. Returns `true` to allow, or a path to redirect to.
 */
export function authGuard(to: RouteLocationNormalized): true | string {
  const { isLoggedIn } = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth === true)

  if (requiresAuth && !isLoggedIn) {
    // Remembered so signing in returns them to what they actually clicked,
    // rather than dumping everyone on the dashboard.
    rememberRoute(to.fullPath)
    return LOGIN_ROUTE
  }

  // A signed-in user on an auth screen is a back-button artefact, not an intent
  // to sign in again.
  const allowAuthenticated = to.matched.some((record) => record.meta.allowAuthenticated === true)
  if (!requiresAuth && isLoggedIn && to.path.startsWith('/auth/') && !allowAuthenticated) {
    return '/'
  }

  return true
}

export function devOnlyGuard(
  to: RouteLocationNormalized,
  isDev: boolean = DEV_TOOLS,
): true | string {
  return !isDev && to.matched.some((record) => record.meta.devOnly === true) ? '/' : true
}

router.beforeEach((to) => devOnlyGuard(to))
router.beforeEach(authGuard)

// `handleHotUpdate` writes to `import.meta.hot.data`, which vitest defines as
// undefined — without this the whole plugin barrel becomes unimportable from a
// test, taking `http` and every api module down with it.
if (import.meta.hot?.data) {
  handleHotUpdate(router)
}
